import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkFirstInvoiceModalApi } from '~/api';
import AnimatingDots from '~/components/AnimatingDots';
import BillingBanner from '~/components/BillingBanner';
import Button from '~/components/Button';
import Gap from '~/components/Gap';
import Header from '~/components/Header';
import MainContainer from '~/components/MainContainer';
import PaginationBar from '~/components/PaginationBar';
import SideModal from '~/components/SideModal';
import { MobileCtaContext } from '~/components/SidebarLayout';
import { ADD_INVOICE_ROUTE, BILLING_ROUTE } from '~/constants/routes';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { analytics, ANALYTICS_EVENTS } from '~/services/analyticsService';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import AddClientModal from '../Client/AddClient/AddClientModal';
import DeleteInvoice from './DeleteInvoice';
import EmptyInvoiceList from './EmptyInvoiceList';
import FirstInvoiceModal from './FirstInvoiceModal';
import InvoiceDetails from './InvoiceDetails';
import InvoiceList from './InvoiceList';
import InvoiceListFilters from './InvoiceListFilters';
import InvoiceTable from './InvoiceTable';
import SendInvoiceModal from './SendInvoiceModal';
import { useInvoiceList } from './hooks/useInvoiceList';
import { InvoiceListContext } from './hooks/useInvoiceListContext';

const Invoice = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [firstInvoiceModalUuid, setFirstInvoiceModalUuid] = useState<string | null>(null);
  const [firstInvoiceClientUuid, setFirstInvoiceClientUuid] = useState<string | null>(null);
  const [sendInvoiceModalUuid, setSendInvoiceModalUuid] = useState<string | null>(null);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);

  const handleAddInvoice = () => {
    analytics.capture(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      screen_name: 'Invoices',
      button_name: 'New Invoice',
      button_id: 'new_invoice',
      section: 'Header',
    });

    if (canCreateInvoice) {
      navigate(ADD_INVOICE_ROUTE);
    } else {
      navigate(`${BILLING_ROUTE}?ref=invoices`);
    }
  };

  const handleBillingBannerClick = () => {
    analytics.capture(ANALYTICS_EVENTS.LINK_CLICKED, {
      screen_name: 'Invoices',
      link_name: 'Upgrade now',
      link_id: 'upgrade_banner',
      section: 'Banner',
    });
    navigate(`${BILLING_ROUTE}?ref=invoices`);
  };

  const { setMobileCta } = useContext(MobileCtaContext);

  const {
    isLoading,
    listData,
    listIds,
    totalPages,
    totalCount,
    currentPage,
    fetchSpecificPage,
    deleteInvoiceId,
    setDeleteInvoiceId,
    onEditListInvoice,
    viewInvoiceId,
    handleStatusChange,
    setViewInvoiceId,
    canCreateInvoice,
    onCloneInvoice,
  } = useInvoiceList();

  function onOpenDeleteInvoiceModal(invoiceUuid: string) {
    setDeleteInvoiceId(invoiceUuid);
  }

  function onOpenViewInvoiceModal(invoiceUuid: string) {
    setViewInvoiceId(invoiceUuid);
  }

  function onEditInvoice(invoiceUuid: string) {
    navigate(`${ADD_INVOICE_ROUTE}?editUuid=${invoiceUuid}`);
  }

  const checkFirstInvoiceModal = async () => {
    try {
      const promise = checkFirstInvoiceModalApi();
      const response = await apiResponseHandler<{
        status: string;
        invoice_uuid: string | null;
        client_uuid: string | null;
      }>(promise);

      if (
        response.type === ApiResponseEnum.Success &&
        response.data.status === 'SHOW' &&
        response.data.invoice_uuid
      ) {
        setFirstInvoiceModalUuid(response.data.invoice_uuid);
        setFirstInvoiceClientUuid(response.data.client_uuid);
      }
    } catch (error) {
      // Fail silently as per requirements
      console.error('First invoice modal check failed:', error);
    }
  };

  useEffect(() => {
    setMobileCta(
      <Button variant="primary" size="sm" className="full-width" onClick={handleAddInvoice}>
        + New Invoice
      </Button>,
    );
    return () => setMobileCta(null);
  }, [canCreateInvoice, navigate]);

  useEffect(() => {
    checkFirstInvoiceModal();
  }, []);

  return (
    <div>
      <Header
        headerText="Invoices"
        rightJsx={
          isDesktop && (
            <Button variant="primary" size="smd" onClick={handleAddInvoice}>
              + New Invoice
            </Button>
          )
        }
      />

      <MainContainer>
        {!canCreateInvoice && (
          <BillingBanner
            variant="error"
            title="Invoice Limit Reached. Ready to Grow? 🚀"
            description="You've successfully created invoices on the Free Plan, great job! Upgrade now to keep creating invoices and continue growing your business."
            buttonText="Upgrade now"
            onButtonClick={handleBillingBannerClick}
          />
        )}

        <div>
          <FirstInvoiceModal
            isModalOpen={firstInvoiceModalUuid !== null}
            onCloseModal={() => setFirstInvoiceModalUuid(null)}
            invoiceUuid={firstInvoiceModalUuid || ''}
            onOpenSendModal={() => {
              if (firstInvoiceModalUuid) {
                setSendInvoiceModalUuid(firstInvoiceModalUuid);
              }
            }}
          />

          <AddClientModal
            isModalOpen={isClientModalOpen}
            onCloseClientModal={() => setIsClientModalOpen(false)}
            heading="Edit Client"
            clientId={firstInvoiceClientUuid}
          />

          <SendInvoiceModal
            isModalOpen={sendInvoiceModalUuid !== null}
            onCloseModal={() => setSendInvoiceModalUuid(null)}
            invoiceUuid={sendInvoiceModalUuid || ''}
            onOpenClientModal={() => setIsClientModalOpen(true)}
            successCallback={() => {
              if (firstInvoiceModalUuid) {
                onEditListInvoice(firstInvoiceModalUuid, { status: 'Sent' });
              }
            }}
          />

          <DeleteInvoice
            invoiceUuid={deleteInvoiceId}
            handleClose={() => {
              setDeleteInvoiceId(null);
            }}
          />

          <InvoiceListContext.Provider
            value={{
              listIds,
              listData,
              onOpenDeleteInvoiceModal,
              onEditInvoice,
              onEditListInvoice,
              onCloneInvoice,
              onOpenViewInvoiceModal,
              handleStatusChange,
              maxListItems: null,
              renderFilters: true,
            }}
          >
            <div className="full-width">
              <SideModal
                size="sm"
                isOpen={viewInvoiceId !== null}
                onClose={() => {
                  setViewInvoiceId(null);
                }}
                children={<InvoiceDetails invoiceUuid={viewInvoiceId} />}
                heading={{ text: 'Invoice Details' }}
              />
              {isDesktop && <InvoiceListFilters />}

              {!isLoading && listIds.length === 0 && <EmptyInvoiceList />}

              {listIds.length > 0 && (
                <>
                  {isDesktop ? <InvoiceTable /> : <InvoiceList />}

                  <PaginationBar
                    totalItems={totalCount}
                    totalPages={totalPages}
                    itemsPerPage={10}
                    currentPage={currentPage}
                    onPageChange={fetchSpecificPage}
                    onItemsPerPageChange={() => {}}
                  />
                </>
              )}
            </div>
          </InvoiceListContext.Provider>

          {isLoading && (
            <div className="full-width center">
              <AnimatingDots />
            </div>
          )}

          <Gap t={32} />
        </div>
      </MainContainer>
    </div>
  );
};

export default Invoice;
